import { memo } from "react";

import ebayLogo from "../../../../../../../../../../assets/system/logos/ebay-logo.png";
import EllipsisMenu from "../../../../../../../../../../components/system/ellipsis-menu/EllipsisMenu.js";
import { normalizeForCompare } from "../../../../../../../../../../utils/textNormalization.js";
import IconBtn from "../../../../../../../../../../components/btns/icon-btn/IconBtn.js";

import {
  ParallelPanelsContainer,
  VariationSection,
  VariationRow,
  ParallelColorSwatch,
  VariationName,
  QtyTickerContainer,
  QtyLabel,
  QtyBtn,
  SerialInputWrapper,
  SerialInput,
  SerialSupplyLabel,
} from "./ParallelPanels.styles.js";



function toTitleCase(str) {
  if (!str || typeof str !== "string") return str;
  return str
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function VariationItem({
  variation,
  isCollected,
  isSelected,
  onSelect,
  qtyValue,
  onQtyChange,
  onAddOrRemove,
  onEbayClick,
  showActions,
  maxRemovable,
  onAddToWhatnot,
  onAddToEbay,
  onAddToDiscord,
  onAddToShowcase,
  serialValue,
  onSerialChange,
}) {
  const name = variation.cardParallelName ?? variation.name;
  const displayNameBase = toTitleCase(name);
  const supply = variation.cardSupply ?? variation.limitedTo;
  // For uncollected variations the supply is already shown in the swatch and serial input,
  // so only append print/supply info for collected cards.
  const displayName =
    isCollected && variation.cardPrintNumber != null && supply
      ? `${displayNameBase} #${variation.cardPrintNumber}/${supply}`
      : displayNameBase;

  const parallelColor = variation.cardColor ?? variation.color ?? "#FFFFFF";
  const isSerialType = supply != null;

  // For serial-numbered uncollected parallels, ADD is only active when the input has a valid value.
  // For serial-numbered collected parallels, REMOVE is always active (print number already known).
  // For non-serial parallels, active whenever qtyValue is non-zero.
  const serialNum = parseInt(serialValue, 10);
  const serialIsValid = !isNaN(serialNum) && serialNum >= 1 && serialNum <= supply;
  let isAddOrRemoveActive;
  if (isSerialType) {
    isAddOrRemoveActive = isCollected ? qtyValue < 0 : serialIsValid;
  } else {
    isAddOrRemoveActive = qtyValue !== 0;
  }

  const isMinusDisabled = isCollected
    ? qtyValue < 0 && Math.abs(qtyValue) >= (maxRemovable ?? 0)
    : qtyValue <= 1;

  const isSerialNumbered =
    variation.cardPrintNumber != null || isSerialType;
  const swatchLabel = isCollected
    ? isSerialNumbered
      ? "-"
      : `x${variation.cardQty ?? 0}`
    : supply
      ? `/${supply}`
      : "x0";

  const parallelForEbay = {
    cardParallelName: name,
    cardColor: parallelColor,
    cardPrintNumber: variation.cardPrintNumber ?? null,
    cardSupply: variation.cardSupply ?? variation.limitedTo ?? null,
  };

  return (
    <VariationRow
      $isCollected={isCollected}
      $isSelected={isSelected}
      onClick={isCollected ? () => onSelect?.(variation) : undefined}
      $clickable={isCollected}
    >
      <ParallelColorSwatch $color={parallelColor} style={{ opacity: isCollected ? 1 : 0.45 }}>
        {swatchLabel}
      </ParallelColorSwatch>
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <VariationName>{displayName}</VariationName>
      </div>

      {showActions && (
        <>
          {isSerialType && !isCollected && (
            <SerialInputWrapper
              onClick={(e) => e.stopPropagation()}
              style={{ opacity: serialIsValid ? 1 : 0.4 }}
            >
              <SerialInput
                type="number"
                min={1}
                max={supply}
                value={serialValue ?? ''}
                placeholder="—"
                onChange={(e) => onSerialChange?.(e.target.value)}
              />
              <SerialSupplyLabel>/ {supply}</SerialSupplyLabel>
            </SerialInputWrapper>
          )}
          <QtyTickerContainer
            onClick={(e) => e.stopPropagation()}
            style={{ opacity: (isSerialType && !isCollected && !serialIsValid) ? 0.4 : 1 }}
          >
            {!isSerialType && (
              <QtyBtn
                type="button"
                disabled={isMinusDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isMinusDisabled) onQtyChange?.(name, -1);
                }}
              >
                -
              </QtyBtn>
            )}
            <QtyLabel
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === "Enter" &&
                isAddOrRemoveActive &&
                onAddOrRemove?.(variation)
              }
              onClick={() => {
                if (isAddOrRemoveActive) onAddOrRemove?.(variation);
              }}
              $active={isAddOrRemoveActive}
            >
              {qtyValue > 0
                ? `ADD ${isSerialType ? 1 : qtyValue}`
                : qtyValue < 0
                  ? `REMOVE ${isSerialType ? 1 : Math.abs(qtyValue)}`
                  : "—"}
            </QtyLabel>
            {!isSerialType && (
              <QtyBtn
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onQtyChange?.(name, 1);
                }}
              >
                +
              </QtyBtn>
            )}
          </QtyTickerContainer>
        </>
      )}
      <div onClick={(e) => e.stopPropagation()}>
        <IconBtn
          variant="logo"
          size="md"
          label="View eBay Sold"
          title="View eBay Sold"
          onClick={() => onEbayClick?.(null, parallelForEbay)}
        >
          <img src={ebayLogo} alt="eBay" />
        </IconBtn>
      </div>
      {isCollected && (
        <div onClick={(e) => e.stopPropagation()}>
          <EllipsisMenu ariaLabel="Variation options">
            <ul>
              <li>Edit</li>
              <li onClick={() => onAddToWhatnot?.(variation)}>Add to Whatnot</li>
              <li onClick={() => onAddToEbay?.(variation)}>Add to eBay</li>
              <li onClick={() => onAddToDiscord?.(variation)}>Add to Discord</li>
              <li onClick={() => onAddToShowcase?.(variation)}>Add to Showcase</li>
            </ul>
          </EllipsisMenu>
        </div>
      )}
    </VariationRow>
  );
}

function ParallelPanelInner({
  collectedCards = [],
  uncollectedVariations = [],
  selectedVariation,
  onVariationSelect,
  variationQtyValues = {},
  onVariationQtyChange,
  onVariationAddOrRemove,
  onEbayClick,
  showcaseView = false,
  dispatchCardToQueue,
  handleRemoveCard,
  onAddToWhatnot,
  onAddToEbay,
  onAddToDiscord,
  onAddToShowcase,
  variationSerialValues = {},
  onVariationSerialChange,
}) {
  const showActions =
    !showcaseView && (dispatchCardToQueue || handleRemoveCard);

  const toVariationShape = (parallel) => ({
    cardParallelName: parallel.name,
    cardColor: parallel.color,
    cardQty: 1,
    cardPrintNumber: null,
    cardSupply: parallel.limitedTo ?? null,
    cardOdds: parallel.odds ?? null,
  });

  return (
    <ParallelPanelsContainer>
      {collectedCards.length > 0 && (
        <VariationSection>
          <h2>COLLECTED</h2>
          {collectedCards.map((parallel, i) => {
            const key = normalizeForCompare(parallel.cardParallelName);
            return (
              <VariationItem
                key={`collected-${parallel.cardParallelName}-${parallel.cardPrintNumber ?? i}`}
                variation={parallel}
                isCollected
                isSelected={
                  normalizeForCompare(selectedVariation?.cardParallelName) === normalizeForCompare(parallel.cardParallelName)
                }
                onSelect={onVariationSelect}
                qtyValue={variationQtyValues[key] ?? -1}
                onQtyChange={onVariationQtyChange}
                onAddOrRemove={onVariationAddOrRemove}
                onEbayClick={onEbayClick}
                showActions={showActions && !!dispatchCardToQueue && !!handleRemoveCard}
                maxRemovable={parallel.cardQty || 0}
                onAddToWhatnot={onAddToWhatnot}
                onAddToEbay={onAddToEbay}
                onAddToDiscord={onAddToDiscord}
                onAddToShowcase={onAddToShowcase}
                serialValue={variationSerialValues[key] ?? ''}
                onSerialChange={(value) => onVariationSerialChange?.(parallel.cardParallelName, value)}
              />
            );
          })}
        </VariationSection>
      )}
      {uncollectedVariations.length > 0 && (
        <VariationSection>
          <h2>UNCOLLECTED</h2>
          {uncollectedVariations.map((parallel) => {
            const key = normalizeForCompare(parallel.name);
            return (
              <VariationItem
                key={parallel.name}
                variation={toVariationShape(parallel)}
                isCollected={false}
                qtyValue={variationQtyValues[key] || 1}
                onQtyChange={onVariationQtyChange}
                onAddOrRemove={onVariationAddOrRemove}
                onEbayClick={onEbayClick}
                showActions={showActions && !!dispatchCardToQueue}
                serialValue={variationSerialValues[key] ?? ''}
                onSerialChange={(value) => onVariationSerialChange?.(parallel.name, value)}
              />
            );
          })}
        </VariationSection>
      )}
    </ParallelPanelsContainer>
  );
}

export default memo(ParallelPanelInner);
